/**
 * @file Décrit le brouillon d'identité client saisi dans une réservation et ses règles de complétude.
 * @module packages.common.components.customer.utils.customerIdentity
 *
 * Un « brouillon » est une fiche client en cours de saisie, sans identifiant :
 * les champs tapés servent à la fois de recherche parmi les clients connus et
 * de données de création si aucun ne correspond. Le backend crée la fiche au
 * moment de la réservation (résolution par email puis téléphone), ce module
 * porte donc la même règle de complétude que lui : un nom et un moyen de contact.
 *
 * Le serveur ne réutilise une fiche trouvée par contact que si le nom coïncide :
 * un email partagé sous un autre nom donne une nouvelle fiche. Les prédicats de
 * contact exact servent à le montrer avant la réservation.
 */

import {validatePhone} from 'packages/common/components/phone-input';
import {DEFAULT_LANGUAGE} from 'packages/common/locales/languageConfig';

/** Champs d'une fiche que le brouillon transporte. */
export const CUSTOMER_DRAFT_FIELDS = Object.freeze(['firstName', 'lastName', 'email', 'phone', 'preferredLanguage']);

/** Longueur minimale de saisie utile avant d'interroger le serveur. */
export const CUSTOMER_SEARCH_MIN_LENGTH = 2;

const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const trimmed = (value) => (typeof value === 'string' ? value.trim() : '');
const digitsOf = (value) => (typeof value === 'string' ? value.replace(/\D/g, '') : '');
const phoneDigitsOf = (entry) => digitsOf(`${entry?.phonePrefix || ''}${entry?.phoneNumber || ''}`);

/**
 * Indique si un email a la forme d'une adresse complète.
 *
 * @param {string} email - Email saisi
 * @return {boolean} true si l'adresse est bien formée
 */
export const isWellFormedEmail = (email) => EMAIL_FORMAT_REGEX.test(trimmed(email));

/**
 * Construit un brouillon normalisé à partir d'une valeur hétérogène
 * (fiche serveur, détails de formulaire, null).
 *
 * @param {Object|null} value - Valeur source
 * @return {Object} Brouillon avec les cinq champs toujours définis
 */
export const toCustomerDraft = (value) => ({
  firstName: value?.firstName || '',
  lastName: value?.lastName || '',
  email: value?.email || '',
  phone: value?.phone || '',
  preferredLanguage: value?.preferredLanguage || DEFAULT_LANGUAGE,
});

/**
 * Indique si l'utilisateur a commencé à saisir une identité.
 *
 * @param {Object|null} details - Détails client
 * @return {boolean} true si au moins un champ d'identité ou de contact est renseigné
 */
export const hasCustomerInput = (details) =>
  Boolean(trimmed(details?.firstName) || trimmed(details?.lastName) || trimmed(details?.email) || trimmed(details?.phone));

/**
 * Indique si la réservation peut partir avec ce client.
 *
 * Une fiche liée (identifiant présent) est toujours complète : les clients
 * importés n'ont parfois qu'un prénom. Un brouillon doit porter un nom et un
 * moyen de contact, règle appliquée par le service de résolution côté serveur.
 *
 * @param {Object|null} details - Détails client (brouillon ou snapshot de fiche)
 * @param {string} [customerId] - Identifiant de la fiche liée
 * @return {boolean} true si le client est exploitable pour créer la réservation
 */
export const isCustomerIdentityComplete = (details, customerId) => {
  if (customerId) return true;
  return Boolean(trimmed(details?.lastName)) && Boolean(trimmed(details?.email) || trimmed(details?.phone));
};

/**
 * Assemble la saisie des champs en un terme de recherche.
 *
 * Le tokenizer serveur découpe la chaîne en mots et exige que chaque mot
 * préfixe un token indexé (nom, email, suffixes de téléphone) : juxtaposer les
 * champs resserre donc la recherche à chaque champ rempli, quel que soit celui
 * dans lequel on tape.
 *
 * @param {Object} draft - Brouillon d'identité
 * @return {string} Terme de recherche, vide si la saisie est trop courte
 */
export const buildCustomerSearchTerm = (draft) => {
  const term = [draft?.firstName, draft?.lastName, draft?.email, draft?.phone]
      .map(trimmed)
      .filter(Boolean)
      .join(' ');
  const meaningful = term.replace(/[^\p{L}\p{N}]/gu, '');
  return meaningful.length >= CUSTOMER_SEARCH_MIN_LENGTH ? term : '';
};

/**
 * Extrait les moyens de contact complets du brouillon, seuls assez fiables
 * pour désigner une fiche existante quand le nom saisi ne correspond pas.
 *
 * Le nom et le prénom n'y figurent jamais : « Jean » seul désigne des dizaines
 * de fiches, un email ou un numéro entier n'en désigne presque toujours qu'une.
 * Un contact en cours de frappe est ignoré pour ne pas interroger le serveur
 * sur un début d'adresse ou de numéro.
 *
 * @param {Object} draft - Brouillon d'identité
 * @return {{email: string, phone: string}} Termes de recherche, vides si le contact est incomplet
 */
export const buildCustomerContactTerms = (draft) => {
  const email = trimmed(draft?.email);
  const phone = trimmed(draft?.phone);
  return {
    email: isWellFormedEmail(email) ? email : '',
    phone: validatePhone(phone) ? phone : '',
  };
};

/**
 * Indique si une fiche utilise l'email du brouillon (principal ou additionnel).
 *
 * @param {Object} customer - Fiche candidate
 * @param {Object} draft - Brouillon saisi
 * @return {boolean} true si l'email coïncide exactement
 */
export const sharesDraftEmail = (customer, draft) => {
  const email = trimmed(draft?.email).toLowerCase();
  if (!email) return false;
  const emails = [customer?.email, ...(customer?.additionalEmails || []).map((entry) => entry?.email)];
  return emails.some((candidate) => typeof candidate === 'string' && candidate.toLowerCase() === email);
};

/**
 * Indique si une fiche utilise le téléphone du brouillon (principal ou additionnel).
 *
 * @param {Object} customer - Fiche candidate
 * @param {Object} draft - Brouillon saisi (téléphone au format E.164)
 * @return {boolean} true si le numéro coïncide exactement
 */
export const sharesDraftPhone = (customer, draft) => {
  const phone = digitsOf(draft?.phone);
  if (!phone) return false;
  const phones = [phoneDigitsOf(customer) || digitsOf(customer?.phone), ...(customer?.additionalPhones || []).map(phoneDigitsOf)];
  return phones.includes(phone);
};

/**
 * Indique si une fiche partage exactement un moyen de contact avec le brouillon.
 *
 * @param {Object} customer - Fiche candidate
 * @param {Object} draft - Brouillon saisi
 * @return {boolean} true si l'email ou le téléphone coïncide
 */
export const isExactContactMatch = (customer, draft) => sharesDraftEmail(customer, draft) || sharesDraftPhone(customer, draft);
